import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { TextDisplayCard } from 'components/TextDisplayCard/TextDisplayCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import useModalClose from 'hooks/useModalClose'
import { CursorPointer, Flex, ItemsGrid, ModalScrollContainer, ThinSeparator } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import {
    IInventoryTransferControl,
    IInventoryTransferTimelineStep,
    IViewInventoryTransferModalProps,
} from 'types/inventoryControl.interface'
import { formatDateForDisplay } from 'utils/helpers'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { TRANSFER_RECEIPT_OK_NOTE, TRANSFER_STATUS_META } from '../inventoryControlDemoData'
import {
    ReceiptActionWrap,
    TimelineDot,
    TimelineLabel,
    TimelineNote,
    TimelineRow,
    TimelineTime,
    TimelineWrap,
} from './ViewInventoryTransferModal.styled'

const buildTimeline = (control: IInventoryTransferControl): IInventoryTransferTimelineStep[] => {
    const order: IInventoryTransferControl['status'][] = ['DISPATCHED', 'IN_TRANSIT', 'RECEIVED']
    const statusIdx = order.indexOf(control.status)
    return order.map((key, idx) => {
        const state: IInventoryTransferTimelineStep['state'] =
            idx < statusIdx
                ? 'done'
                : idx === statusIdx
                    ? key === 'IN_TRANSIT' ? 'active' : 'done'
                    : 'pending'
        let time: string | null = null
        if (key === 'DISPATCHED' && statusIdx >= 0) time = control.dispatchedAt
        if (key === 'IN_TRANSIT' && control.status === 'IN_TRANSIT') time = `On the road since ${control.dispatchedAt ?? '—'}`
        if (key === 'RECEIVED' && control.receivedAt) {
            time = control.receivedBy ? `${control.receivedAt} · ${control.receivedBy}` : control.receivedAt
        }
        return { key, label: TRANSFER_STATUS_META[key].label, state, time }
    })
}

export const ViewInventoryTransferModal = ({ closeModal, value, onConfirmReceipt }: IViewInventoryTransferModalProps) => {
    useFocusFirstInput()
    useModalClose(closeModal)

    const control = value.control
    const meta = control ? TRANSFER_STATUS_META[control.status] : null

    return <Modal black>
        <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <Flex center gap='0.75rem'>
                <TitleText weight='bold' >Transfer Preview</TitleText>
                {meta && (
                    <StatusPill status={meta.tone} dot={control.status === 'IN_TRANSIT'}>
                        {meta.label}
                    </StatusPill>
                )}
            </Flex>
            <CursorPointer onClick={closeModal} >
                <CloseIcon />
            </CursorPointer>
        </Flex>
        <ModalScrollContainer>
            <ItemsGrid columns={2} >
                <TextDisplayCard title={formatDateForDisplay(value.date)} labelText='Date' />
                <TextDisplayCard title={value.performedByUser ? value?.performedByUser?.firstName + ' ' + value?.performedByUser?.lastName : '/'} labelText='Performed By' />
                <TextDisplayCard title={value.assignedForUser ? value?.assignedForUser?.firstName + ' ' + value?.assignedForUser?.lastName : '/'} labelText='Assigned Employee' />
                <TextDisplayCard title={value.fromLocation ? value.fromLocation.name : '/'} labelText='From location' />
                <TextDisplayCard title={value.toLocation ? value.toLocation.name : '/'} labelText='To location' />
            </ItemsGrid>

            <TextDisplayCard style={{ marginTop: '1rem' }} title={value.notes || '/'} labelText='Notes' width='100%' />

            {control && (
                <>
                    <ThinSeparator />
                    <Flex column gap='1rem'>
                        <BodyText weight='bold' >Custody:</BodyText>
                        <TimelineWrap>
                            {buildTimeline(control).map((step) => (
                                <TimelineRow key={step.key}>
                                    <TimelineDot $state={step.state} />
                                    <TimelineLabel $dim={step.state === 'pending'}>{step.label}</TimelineLabel>
                                    <TimelineTime $dim={step.state === 'pending'}>{step.time ?? '—'}</TimelineTime>
                                </TimelineRow>
                            ))}
                            {control.status === 'RECEIVED' && (
                                <TimelineNote $warning={!!control.varianceNote && control.varianceNote !== TRANSFER_RECEIPT_OK_NOTE}>
                                    {control.varianceNote ?? 'No variance'}
                                </TimelineNote>
                            )}
                        </TimelineWrap>
                        {control.status !== 'RECEIVED' && onConfirmReceipt && (
                            <ReceiptActionWrap>
                                <OtherText grey>Counted at destination — confirm to close custody</OtherText>
                                <Button size='sm' variant='primary' onClick={() => onConfirmReceipt(value)}>
                                    Confirm receipt
                                </Button>
                            </ReceiptActionWrap>
                        )}
                    </Flex>
                </>
            )}

            <ThinSeparator />
            <Flex column gap='1rem'  >
                <BodyText weight='bold' >Items:</BodyText>
                {value.lines.map(l => <Flex key={l.id} column width='100%'>
                    <Flex width='100%' gap='1rem'  >
                        <TextDisplayCard width='100%' title={l.inventoryItem ? l.inventoryItem.name : '/'} labelText='Item' />
                        <TextDisplayCard width='100%' title={l.quantity} labelText='Quantity' />
                    </Flex>
                    <TextDisplayCard style={{ marginTop: '1rem' }} title={l.lineNotes || '/'} labelText='Notes' width='100%' />
                    <ThinSeparator margin='0.5rem 0' />
                </Flex>)}
            </Flex>


        </ModalScrollContainer>
    </Modal>
}
